import { useGetMyBookingsQuery } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, MailIcon, PhoneIcon, Plus } from 'lucide-react'
import { useState } from 'react'

import { BookingList } from '@/components/booking-list/booking-list'
import { ImportReservationDialog } from '@/components/import-reservation/import-reservation-dialog'
import { CloudyCopiableContactCard } from '@/components/payment-confirmation/cloudy-copiable-contact-card'
import {
  WhatsappCallCloudyCard,
  WhatsappMessageCloudyCard,
} from '@/components/payment-confirmation/whatsapp-components'
import { AGENCY_EMAIL, AGENCY_PHONE } from '@/lib/constants/agency'
import { useAuthStore } from '@/stores/auth.store'

export const Route = createFileRoute('/_authenticated/_layout/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user } = useAuthStore()
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const { data: bookings = [], isLoading: isLoadingBookings } = useGetMyBookingsQuery()

  return (
    <div className="flex min-h-screen flex-col">
      {/* Hero Banner */}
      <section className="bg-brand-600 relative overflow-hidden px-6 py-8 md:px-28 md:py-10">
        <img
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-50"
          src="/images/customer-arear-background.jpg"
        />
        <div className="relative z-10 flex flex-col gap-2">
          <h1 className="text-3xl font-semibold tracking-tight text-white md:text-5xl">
            Bonjour {user?.firstName}
          </h1>
          <p className="text-sm font-medium text-white">
            Ajoutez vos réservations à votre compte ou modifiez vos données personnelles
          </p>
        </div>
      </section>

      {/* Content Area */}
      <main className="flex flex-1 flex-col gap-8 bg-white px-6 py-8 md:px-28 md:py-10">
        {/* Booking Section */}
        {isLoadingBookings ? (
          <div className="flex items-center justify-center py-14">
            <Loader2 className="text-brand-600 size-8 animate-spin" />
          </div>
        ) : bookings.length > 0 ? (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center justify-between md:flex-row">
              <h2 className="text-gray-light-900 text-xl font-semibold">Mes réservations</h2>
              <Button
                className="bg-brand-600 hover:bg-brand-700 gap-1 text-white"
                onClick={() => setIsImportDialogOpen(true)}
              >
                <Plus className="size-5" />
                Importer une réservation
              </Button>
            </div>
            <BookingList bookings={bookings} />
          </div>
        ) : (
          /* Empty State Card */
          <div className="border-gray-light-200 flex flex-col items-center rounded-3xl border px-8 py-14">
            <img
              alt="Avion"
              className="mb-6 h-38 w-61 rounded-4xl object-cover"
              src="/images/empty-state-airplane.jpg"
            />
            <h2 className="text-gray-light-900 mb-6 text-center text-base font-semibold">
              Vous n&apos;avez pas encore de réservation
            </h2>
            <Button
              className="bg-brand-600 hover:bg-brand-700 gap-1 text-white"
              onClick={() => setIsImportDialogOpen(true)}
            >
              <Plus className="size-5" />
              Importer une réservation
            </Button>
          </div>
        )}

        {/* Contact + CTA Section */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Contact Cards Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:flex-1">
            <CloudyCopiableContactCard
              icon={PhoneIcon}
              label="Téléphone"
              title="Nous appeler !"
              value={AGENCY_PHONE}
            />
            <CloudyCopiableContactCard
              icon={MailIcon}
              label="Adresse email"
              title="Par email"
              value={AGENCY_EMAIL}
            />
            <WhatsappCallCloudyCard />
            <WhatsappMessageCloudyCard />
          </div>

          {/* CTA Card */}
          <div className="bg-brand-500 relative flex flex-col justify-end overflow-hidden rounded-lg p-6 lg:flex-1">
            <img
              alt=""
              className="pointer-events-none absolute top-0 left-0 rounded-tl-lg"
              src="/icons/top-left-cloud.svg"
            />
            <img
              alt=""
              className="pointer-events-none absolute right-0 bottom-0 rounded-br-lg"
              src="/icons/bottom-right-cloud.svg"
            />
            <div className="relative z-10 mb-4">
              <img alt="Avion" className="w-48" src="/images/plane.svg" />
            </div>
            <div className="relative z-10 flex flex-col gap-2">
              <h3 className="text-2xl font-semibold text-white">Une envie de voyage ?</h3>
              <p className="text-sm text-white">
                Trouvez la destination de vos rêves dès maintenant avec Massilia Voyages.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Import Reservation Dialog */}
      <ImportReservationDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
      />
    </div>
  )
}
